/**
 * Per-row action menu for the gated Hierarchy editor (PLT-5691) — the
 * click / keyboard-accessible complement to PR 5a's drag-and-drop reparenting.
 *
 * A single Primer `ActionMenu` whose overlay switches between two modes:
 *  - `menu`   — Edit / Duplicate / Delete plus Add child product / Add child
 *               segment / Add parent segment / Change parent;
 *  - `picker` — a filterable list of valid parent nodes (+ "Top level") that
 *               calls `onReparent`.
 *
 * Parent candidates and the add-child affordances are filtered through the same
 * `assertHierarchyValid` guard the backend enforces (cycles + depth cap), so the
 * menu never offers an action that would be rejected on save.
 */
import { ActionList, ActionMenu, Button, Dialog, TextInput } from '@primer/react';
import {
  ArrowSwitchIcon,
  ArrowUpIcon,
  CopyIcon,
  FileDirectoryIcon,
  KebabHorizontalIcon,
  LinkIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@primer/octicons-react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { assertHierarchyValid } from '@upright/common/model/input/blanket/blanket-product/hierarchy-utils';
import { PiuProductState } from '@upright/common/model/input/companies/products/product-input-ui/types';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import ModelledIcon from '@upright/ui/icons/img/streamline-mini-line/interface-essential/hierarchy/interface-hierarchy-3.svg';

interface HierarchyRowActionsProps {
  product: PiuProductState;
  products: PiuProductState[];
  // Added segment-editor actions (PLT-5691).
  onAddChildProduct: (parentId: string) => void;
  // "Add child segment" inserts a new empty segment INSIDE this segment (as its child, PLT-5799).
  onAddChildSegment: (parentId: string) => void;
  // "Add parent segment" inserts a new segment as the PARENT of this node (PLT-5707).
  onAddParentSegment: (productId: string) => void;
  onReparent: (productId: string, newParentId: string | null) => void;
  // The standard per-product row actions, kept in parity with the flat List
  // view: Edit (matching + attributes), Duplicate, Delete, and the team-only
  // Internal (modelled-as) item. `disabled` mirrors the flat table's gating.
  onEditProduct: (product: PiuProductState) => void;
  // Team-only "Edit matching" — opens the standalone matching modal (PLT-5707).
  onEditMatching: (product: PiuProductState) => void;
  onDuplicateProduct: (product: PiuProductState) => void;
  onDeleteProduct: (product: PiuProductState) => void;
  onOpenInternal: (product: PiuProductState) => void;
  disabled: boolean;
}

type OverlayMode = 'menu' | 'picker';

// Probe id used only to test whether a hypothetical child would breach the
// depth cap; never added to the real product set.
const DEPTH_PROBE_ID = '__depth-probe__';

function isReparentValid(products: PiuProductState[], productId: string, newParentId: string | null): boolean {
  const tentative = products.map(p => (p.id === productId ? { ...p, parentId: newParentId } : p));
  try {
    assertHierarchyValid(tentative);
    return true;
  } catch {
    return false;
  }
}

export function HierarchyRowActions({
  product,
  products,
  onAddChildProduct,
  onAddChildSegment,
  onAddParentSegment,
  onReparent,
  onEditProduct,
  onEditMatching,
  onDuplicateProduct,
  onDeleteProduct,
  onOpenInternal,
  disabled,
}: HierarchyRowActionsProps) {
  const isTeam = useIsTeam();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<OverlayMode>('menu');
  const [filter, setFilter] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const reset = () => {
    setMode('menu');
    setFilter('');
  };

  // PLT-5841 (Jenna QA): deleting a segment that holds other rows is easy to trigger by accident
  // and re-parents its whole subtree up a level, so confirm first. Count all descendants (not just
  // direct children) so the prompt conveys how much is affected. Leaf products and empty segments
  // stay one-click — only a segment with contents needs the guard (Roni).
  const descendantCount = useMemo(() => {
    const childrenByParent = new Map<string | null, string[]>();
    for (const p of products) {
      const pid = p.parentId ?? null;
      const siblings = childrenByParent.get(pid) ?? [];
      siblings.push(p.id);
      childrenByParent.set(pid, siblings);
    }
    let count = 0;
    const stack = [product.id];
    while (stack.length > 0) {
      const id = stack.pop() as string;
      const kids = childrenByParent.get(id) ?? [];
      count += kids.length;
      stack.push(...kids);
    }
    return count;
  }, [products, product.id]);
  const needsDeleteConfirm = product.isSegment === true && descendantCount > 0;

  // Adding a child deepens the tree by one level under this row; disable the
  // add affordances when that would breach the depth cap.
  const canAddChild = useMemo(() => {
    // `assertHierarchyValid` only reads id / parentId, so validate against the
    // minimal shape plus a probe child of this row rather than full products.
    const tentative = [
      ...products.map(p => ({ id: p.id, parentId: p.parentId ?? null })),
      { id: DEPTH_PROBE_ID, parentId: product.id },
    ];
    try {
      assertHierarchyValid(tentative);
      return true;
    } catch {
      return false;
    }
  }, [products, product.id]);

  // Inserting a segment as this row's parent (PLT-5707) pushes the row and its
  // whole subtree down one level; disable the affordance when that would breach
  // the depth cap. Probe with a hypothetical parent above this row.
  const canAddParent = useMemo(() => {
    const PARENT_PROBE_ID = '__parent-probe__';
    const tentative = [
      ...products.map(p => ({
        id: p.id,
        parentId: p.id === product.id ? PARENT_PROBE_ID : (p.parentId ?? null),
      })),
      { id: PARENT_PROBE_ID, parentId: product.parentId ?? null },
    ];
    try {
      assertHierarchyValid(tentative);
      return true;
    } catch {
      return false;
    }
  }, [products, product.id, product.parentId]);

  // Valid parent candidates for this node, computed only while the picker is
  // open. The current parent is excluded (moving there is a no-op), so "Top
  // level" only appears when the node isn't already top-level. Everything else
  // is filtered through `assertHierarchyValid` (self / descendant-cycle / depth).
  const candidates = useMemo(() => {
    if (mode !== 'picker') return [];
    const currentParent = product.parentId ?? null;
    const list: Array<{ id: string | null; title: string }> = [];
    if (currentParent !== null && isReparentValid(products, product.id, null)) {
      list.push({ id: null, title: 'Top level' });
    }
    for (const candidate of products) {
      if (candidate.id === product.id) continue;
      if (candidate.id === currentParent) continue;
      // PLT-5707: only business units can be parents, so only segments are
      // offered as re-parent targets (plus the "Top level" option above).
      if (candidate.isSegment !== true) continue;
      if (!isReparentValid(products, product.id, candidate.id)) continue;
      list.push({ id: candidate.id, title: candidate.title || '(untitled)' });
    }
    return list;
  }, [mode, products, product.id, product.parentId]);

  const normalizedFilter = filter.trim().toLowerCase();
  const filteredCandidates = normalizedFilter
    ? candidates.filter(c => c.title.toLowerCase().includes(normalizedFilter))
    : candidates;

  const currentParentId = product.parentId ?? null;

  return (
    <>
      <ActionMenu
        open={open}
        onOpenChange={next => {
          setOpen(next);
          if (!next) reset();
        }}
      >
        <ActionMenu.Anchor>
          <IconButton variant="invisible" aria-label="Row actions">
            <KebabHorizontalIcon />
          </IconButton>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay width="medium">
          {mode === 'menu' && (
            <ActionList>
              {/* Standard per-product actions — parity with the flat List view. Segments and leaf
                products both open the same dialog: name, description and (on Enterprise)
                attributes, which for a segment cascade to its descendants. Revenue is edited in
                the table's own "Revenue or %" column, not in the dialog (PLT-6329). Segments
                carry no taxonomy match, so they get no Matching tab and no Internal
                (modelled-as) item. */}
              <ActionList.Item
                disabled={disabled}
                onSelect={() => {
                  onEditProduct(product);
                }}
              >
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                {product.isSegment ? 'Edit segment…' : 'Edit product…'}
              </ActionList.Item>
              <ActionList.Item
                disabled={disabled}
                onSelect={() => {
                  onDuplicateProduct(product);
                }}
              >
                <ActionList.LeadingVisual>
                  <CopyIcon />
                </ActionList.LeadingVisual>
                Duplicate
              </ActionList.Item>
              <ActionList.Item
                disabled={disabled}
                variant="danger"
                onSelect={() => {
                  // Selecting an item closes the ActionMenu; for a non-empty segment open the
                  // confirm dialog instead of deleting immediately (PLT-5841).
                  if (needsDeleteConfirm) {
                    setConfirmingDelete(true);
                  } else {
                    onDeleteProduct(product);
                  }
                }}
              >
                <ActionList.LeadingVisual>
                  <TrashIcon />
                </ActionList.LeadingVisual>
                Delete
              </ActionList.Item>
              {/* Team-only "Edit matching" — matching lives in its own modal now
                (PLT-5707); only leaf products have a taxonomy match. */}
              {isTeam && !product.isSegment && (
                <ActionList.Item
                  disabled={disabled}
                  onSelect={() => {
                    onEditMatching(product);
                  }}
                >
                  <ActionList.LeadingVisual>
                    <LinkIcon />
                  </ActionList.LeadingVisual>
                  Edit matching
                </ActionList.Item>
              )}
              {isTeam && !product.isSegment && (
                <ActionList.Item
                  onSelect={() => {
                    onOpenInternal(product);
                  }}
                >
                  <ActionList.LeadingVisual>
                    <StyledModelledIcon />
                  </ActionList.LeadingVisual>
                  Internal
                </ActionList.Item>
              )}
              <ActionList.Divider />
              {/* Added hierarchy actions (PLT-5691). Respect the same frozen /
                publishing `disabled` state as the standard actions above. */}
              {/* PLT-5707: products are always leaves, so "Add child product" is
                offered only on business-unit (segment) rows. */}
              {product.isSegment && (
                <ActionList.Item
                  disabled={disabled || !canAddChild}
                  title={!disabled && !canAddChild ? 'Maximum depth reached' : undefined}
                  onSelect={() => {
                    onAddChildProduct(product.id);
                  }}
                >
                  <ActionList.LeadingVisual>
                    <PlusIcon />
                  </ActionList.LeadingVisual>
                  Add child product
                </ActionList.Item>
              )}
              {/* "Add child segment" — a new empty segment nested inside this one. Segments only
                (products are always leaves), mirroring "Add child product" (PLT-5799). */}
              {product.isSegment && (
                <ActionList.Item
                  disabled={disabled || !canAddChild}
                  title={!disabled && !canAddChild ? 'Maximum depth reached' : undefined}
                  onSelect={() => {
                    onAddChildSegment(product.id);
                  }}
                >
                  <ActionList.LeadingVisual>
                    <FileDirectoryIcon />
                  </ActionList.LeadingVisual>
                  Add child segment
                </ActionList.Item>
              )}
              {/* "Add parent segment" wraps this node in a new segment (available on both leaf
                products and segments), inheriting the node's current parent (PLT-5799). */}
              <ActionList.Item
                disabled={disabled || !canAddParent}
                title={!disabled && !canAddParent ? 'Maximum depth reached' : undefined}
                onSelect={() => {
                  onAddParentSegment(product.id);
                }}
              >
                <ActionList.LeadingVisual>
                  <FileDirectoryIcon />
                </ActionList.LeadingVisual>
                Add parent segment
              </ActionList.Item>
              {/* Direct "make top-level" reparent (PLT-5707) — the click path Roni
                asked for, avoiding the drag-to-top-zone on long lists. Only shown
                when the row isn't already top-level and the move is valid. */}
              {currentParentId !== null && isReparentValid(products, product.id, null) && (
                <ActionList.Item
                  disabled={disabled}
                  onSelect={() => {
                    onReparent(product.id, null);
                    setOpen(false);
                    reset();
                  }}
                >
                  <ActionList.LeadingVisual>
                    <ArrowUpIcon />
                  </ActionList.LeadingVisual>
                  Make top-level
                </ActionList.Item>
              )}
              <ActionList.Item
                disabled={disabled}
                onSelect={event => {
                  event.preventDefault();
                  setMode('picker');
                }}
              >
                <ActionList.LeadingVisual>
                  <ArrowSwitchIcon />
                </ActionList.LeadingVisual>
                Change parent…
              </ActionList.Item>
            </ActionList>
          )}

          {mode === 'picker' && (
            <>
              <OverlayField>
                <TextInput
                  autoFocus
                  placeholder="Search parents…"
                  value={filter}
                  onChange={e => {
                    setFilter(e.target.value);
                  }}
                  sx={{ width: '100%' }}
                />
              </OverlayField>
              <ActionList selectionVariant="single">
                {filteredCandidates.length === 0 && <ActionList.Item disabled>No valid parents</ActionList.Item>}
                {filteredCandidates.map(candidate => (
                  <ActionList.Item
                    key={candidate.id ?? '__top-level__'}
                    selected={currentParentId === candidate.id}
                    onSelect={() => {
                      onReparent(product.id, candidate.id);
                      setOpen(false);
                      reset();
                    }}
                  >
                    {candidate.title}
                  </ActionList.Item>
                ))}
              </ActionList>
            </>
          )}
        </ActionMenu.Overlay>
      </ActionMenu>
      {confirmingDelete && (
        <Dialog
          returnFocusRef={{ current: null }}
          onClose={() => {
            setConfirmingDelete(false);
          }}
          title="Delete segment?"
          footerButtons={[
            {
              content: 'Cancel',
              buttonType: 'default',
              onClick: () => {
                setConfirmingDelete(false);
              },
            },
            {
              content: 'Delete segment',
              buttonType: 'danger',
              onClick: () => {
                setConfirmingDelete(false);
                onDeleteProduct(product);
              },
            },
          ]}
        >
          <Dialog.Body>
            "{product.title || '(untitled)'}" contains {descendantCount} {descendantCount === 1 ? 'item' : 'items'}.
            Deleting the segment keeps its contents and moves them up one level. Continue?
          </Dialog.Body>
        </Dialog>
      )}
    </>
  );
}

const IconButton = styled(Button)`
  padding: 0 var(--space__sm);
`;

const OverlayField = styled.div`
  padding: var(--space__sm);
  border-bottom: 1px solid var(--borderColor-muted);
`;

const StyledModelledIcon = styled(ModelledIcon)`
  width: 16px;
  height: 16px;
`;
